import React, { useState, useEffect } from "react";
import { MapContainer, Marker, Popup, Circle, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { MAP_CONFIG } from "../../config/api.config";
import BasemapLayer from "./BasemapLayer";
import { T, Btn, Badge } from "../shared/UI";
import { STATUS, fmt, batteryColor, headingLabel } from "../../utils/helpers";
import { useApp } from "../../store/AppContext";
import { officesApi } from "../../services/api.service";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function makeIcon(agent, selected, mapStyle) {
  const s = STATUS[agent.status] || STATUS.offline;
  const sz   = selected ? 40 : 32;
  const ring = sz + 16;
  const isLight = mapStyle === "light";
  const labelBg = isLight ? "rgba(12,19,32,0.9)" : T.s1;
  const labelColor = isLight ? "#ffffff" : T.text;
  const labelBorder = isLight ? "rgba(0,0,0,0.35)" : T.border2;
  const shadow = isLight
    ? `0 0 0 3px rgba(0,0,0,0.25), 0 0 16px rgba(0,0,0,0.35)`
    : (selected ? `0 0 0 4px ${s.color}33,0 0 20px ${s.color}55` : `0 0 12px ${s.color}33`);
  const pulse = agent.status === "sos"
    ? `<div style="position:absolute;inset:-10px;border-radius:50%;border:2px solid ${s.color};animation:pulse 0.8s infinite;opacity:0.7;"></div>`
    : agent.status === "active"
    ? `<div style="position:absolute;inset:-8px;border-radius:50%;border:1px solid ${s.color};animation:pulse 2s infinite;opacity:0.4;"></div>`
    : "";
  
  // Arrow indicator for moving agents - shows direction of movement
  const heading = agent.heading || 0;
  const isMoving = (agent.speed || 0) > 0 && agent.status === "active";
  const arrowSize = selected ? 18 : 14;
  const arrowColor = s.color;
  
  // Position arrow at the top center of the circle, pointing outward
  // Rotate arrow to match heading (0° = North/up, 90° = East/right, etc.)
  const circleTop = 14;
  const circleCenterX = ring / 2;
  const arrowTop = circleTop - arrowSize / 2;
  
  const arrowSvg = isMoving ? `
    <div style="position:absolute;top:${arrowTop}px;left:${circleCenterX}px;transform:translateX(-50%) rotate(${heading}deg);width:${arrowSize}px;height:${arrowSize}px;z-index:2;pointer-events:none;">
      <svg width="${arrowSize}" height="${arrowSize}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L6 10H9V18H15V10H18L12 2Z" fill="${arrowColor}" opacity="0.95" stroke="${isLight ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.4)'}" stroke-width="1.5"/>
      </svg>
    </div>
  ` : "";
  
  return L.divIcon({
    className: "",
    iconSize:   [ring, ring + 22],
    iconAnchor: [ring / 2, ring / 2 + 11],
    popupAnchor:[0, -(ring / 2 + 11)],
    html: `<div style="position:relative;width:${ring}px;height:${ring+22}px;display:flex;flex-direction:column;align-items:center;">
      <div style="position:absolute;top:0;left:50%;transform:translateX(-50%);font-family:'DM Mono',monospace;font-size:9px;font-weight:600;color:${labelColor};background:${labelBg};border:1px solid ${labelBorder};border-radius:20px;padding:2px 7px;white-space:nowrap;max-width:${ring * 1.4}px;overflow:hidden;text-overflow:ellipsis;">${agent.name}</div>
      <div style="position:absolute;top:14px;left:50%;transform:translateX(-50%);width:${ring}px;height:${ring}px;border-radius:50%;display:flex;align-items:center;justify-content:center;">
        ${pulse}
        <div style="width:${sz}px;height:${sz}px;border-radius:50%;background:${s.bg};border:${selected?"2.5px":"2px"} solid ${s.color};display:flex;align-items:center;justify-content:center;font-family:'DM Mono',monospace;font-size:${selected?13:11}px;font-weight:700;color:${s.color};box-shadow:${shadow};position:relative;z-index:1;">${agent.initials}</div>
        ${arrowSvg}
      </div>
    </div>`,
  });
}

function MapFly({ agent }) {
  const map = useMap();
  React.useEffect(() => {
    if (agent?.lat && agent?.lng) {
      map.flyTo([agent.lat, agent.lng], Math.max(map.getZoom(), 14), { duration: 1 });
    }
  }, [agent, map]);
  return null;
}

function MapFit({ agents, shouldFit }) {
  const map = useMap();
  
  React.useEffect(() => {
    // Only fit bounds when shouldFit is true (when filters change)
    if (!shouldFit || !agents || agents.length === 0) return;
    const bounds = agents
      .filter(a => a.lat && a.lng)
      .map(a => [a.lat, a.lng]);
    if (bounds.length === 0) return;
    
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15, animate: true });
  }, [shouldFit, agents, map]);
  
  return null;
}

const ZONE_COLORS = ["#7c6af7", "#6af7c4", "#f76a8a", "#f7c46a"];

export default function MapView({ agents, zones, sendAlert }) {
  const { selectedAgent, selectAgent, openAlertModal, openHistory } = useApp();
  const showZones = true;
  const [mapStyle, setMapStyle] = useState("light");
  const [trailMode] = useState("off"); // off | selected | all
  const [trailById, setTrailById] = useState({});
  const [offices, setOffices] = useState([]);
  const [productFilter, setProductFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [agentSearch, setAgentSearch] = useState("");
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [agentSelectOpen, setAgentSelectOpen] = useState(false);
  const [agentDropdownSearch, setAgentDropdownSearch] = useState("");
  const [shouldFitMap, setShouldFitMap] = useState(true); // Only fit map when filters change or initial load
  const prevFiltersRef = React.useRef({ productFilter: "", statusFilter: "all", selectedAgentIds: [], agentSearch: "" });

  // Find map center: prefer selected agent, then first online agent, then config default
  const liveAgents = agents.filter(a => a.lat && a.lng);
  const productOptions = Array.from(
    agents.reduce((acc, a) => {
      if (a.productId && a.productName) acc.set(String(a.productId), a.productName);
      return acc;
    }, new Map())
  ).map(([id, name]) => ({ id, name }));

  const statusCounts = liveAgents.reduce((acc, a) => {
    if (a.status === "active") {
      acc.online += 1;
      if (a.speed > 0) acc.moving += 1;
      else acc.idle += 1;
    } else {
      acc.offline += 1;
    }
    return acc;
  }, { online: 0, offline: 0, idle: 0, moving: 0 });

  // Get agents filtered by product first (for agent selection dropdown)
  const productFilteredAgents = liveAgents.filter(a => {
    if (productFilter && String(a.productId) !== String(productFilter)) return false;
    return true;
  });

  // Final filtered agents with all filters applied
  const filteredAgents = productFilteredAgents.filter(a => {
    // Status filter
    if (statusFilter === "online") {
      if (a.status !== "active") return false;
    } else if (statusFilter === "offline") {
      if (a.status === "active") return false;
    } else if (statusFilter === "idle") {
      if (a.status !== "active" || (a.speed || 0) !== 0) return false;
    } else if (statusFilter === "moving") {
      if (a.status !== "active" || (a.speed || 0) === 0) return false;
    }
    
    // Agent search filter
    if (agentSearch.trim()) {
      const searchLower = agentSearch.toLowerCase();
      if (!a.name.toLowerCase().includes(searchLower) && 
          !a.mobile.includes(searchLower)) {
        return false;
      }
    }
    
    // Selected agents filter (if any agents are selected, show only those)
    if (selectedAgentIds.length > 0) {
      if (!selectedAgentIds.includes(a.id)) return false;
    }
    
    return true;
  });
  const showTrails = trailMode !== "off";
  const showAllTrails = trailMode === "all";

  useEffect(() => {
    if (!showTrails || liveAgents.length === 0) return;
    const TRAIL_LIMIT = 25;
    setTrailById(prev => {
      const next = { ...prev };
      liveAgents.forEach(agent => {
        const lat = agent.lat;
        const lng = agent.lng;
        if (lat == null || lng == null) return;
        const prevTrail = next[agent.id] ? [...next[agent.id]] : [];
        const last = prevTrail[prevTrail.length - 1];
        if (!last || last.lat !== lat || last.lng !== lng) {
          prevTrail.push({ lat, lng });
          if (prevTrail.length > TRAIL_LIMIT) {
            prevTrail.splice(0, prevTrail.length - TRAIL_LIMIT);
          }
          next[agent.id] = prevTrail;
        }
      });
      return next;
    });
  }, [liveAgents, showTrails]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await officesApi.list();
        const raw = Array.isArray(res.data) ? res.data
          : Array.isArray(res.data?.data) ? res.data.data : [];
        if (mounted) setOffices(raw);
      } catch {
        if (mounted) setOffices([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Reset shouldFitMap after initial fit
  useEffect(() => {
    if (shouldFitMap && filteredAgents.length > 0) {
      // Reset after map has had time to fit
      const timer = setTimeout(() => setShouldFitMap(false), 200);
      return () => clearTimeout(timer);
    }
  }, [shouldFitMap, filteredAgents.length]);

  // Detect filter changes and trigger map fit only when filters change
  useEffect(() => {
    const currentFilters = {
      productFilter,
      statusFilter,
      selectedAgentIds: [...selectedAgentIds].sort().join(','),
      agentSearch
    };
    const prevFilters = prevFiltersRef.current;
    
    const filtersChanged = 
      currentFilters.productFilter !== prevFilters.productFilter ||
      currentFilters.statusFilter !== prevFilters.statusFilter ||
      currentFilters.selectedAgentIds !== prevFilters.selectedAgentIds ||
      currentFilters.agentSearch !== prevFilters.agentSearch;
    
    if (filtersChanged) {
      setShouldFitMap(true);
      prevFiltersRef.current = currentFilters;
    }
  }, [productFilter, statusFilter, selectedAgentIds, agentSearch]);

  // Close agent dropdown when clicking outside
  useEffect(() => {
    if (!agentSelectOpen) return;
    const handleClickOutside = (e) => {
      const target = e.target;
      if (!target.closest('[data-agent-select]')) {
        setAgentSelectOpen(false);
        setAgentDropdownSearch(""); // Clear search when closing
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [agentSelectOpen]);

  return (
    <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
      <MapContainer
        center={MAP_CONFIG.CENTER}
        zoom={MAP_CONFIG.ZOOM}
        style={{ width: "100%", height: "100%" }}
        zoomControl={false}
        maxZoom={20}
      >
        <BasemapLayer mapStyle={mapStyle} />
        <MapFly agent={selectedAgent} />
        <MapFit agents={filteredAgents} shouldFit={shouldFitMap} />

        {/* Geofence zones */}
        {showZones && zones.map((z, i) => (
          <Circle key={z.id} center={[z.lat, z.lng]} radius={z.radius}
            pathOptions={{ color: ZONE_COLORS[i % 4], fillColor: ZONE_COLORS[i % 4], fillOpacity: 0.06, weight: 1.5, dashArray: "6 6" }}>
            <Popup>
              <div style={styles.popup}><strong>{z.name}</strong><br />Radius: {z.radius}m</div>
            </Popup>
          </Circle>
        ))}

        {/* Office locations */}
        {offices.map(o => (
          <Circle
            key={`office-${o.id}`}
            center={[o.coordinates?.latitude, o.coordinates?.longitude]}
            radius={50}
            pathOptions={{ color: T.acc4, fillColor: T.acc4, fillOpacity: 0.15, weight: 1.5 }}
          >
            <Popup>
              <div style={styles.popup}>
                <strong>{o.location}</strong>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{o.address}</div>
              </div>
            </Popup>
          </Circle>
        ))}

        {/* Live trails */}
        {showTrails && (showAllTrails ? liveAgents : selectedAgent ? [selectedAgent] : []).map(agent => {
          const trail = trailById[agent.id] || [];
          if (trail.length < 2) return null;
          const color = STATUS[agent.status]?.color || T.acc;
          return (
            <Polyline
              key={`trail-${agent.id}`}
              positions={trail.map(p => [p.lat, p.lng])}
              pathOptions={{ color, weight: 2, opacity: 0.7 }}
            />
          );
        })}

        {/* Agent markers — only render agents with GPS coords */}
        {filteredAgents.map(agent => {
          const isSel = selectedAgent?.id === agent.id;
          return (
            <Marker
              key={agent.id}
              position={[agent.lat, agent.lng]}
              icon={makeIcon(agent, isSel, mapStyle)}
              zIndexOffset={agent.status === "sos" ? 2000 : isSel ? 1000 : 0}
              eventHandlers={{ click: () => selectAgent(agent) }}
            >
              <Popup>
                <div style={styles.popup}>
                  <strong style={{ fontSize: 14 }}>{agent.name}</strong>
                  <div style={{ color: T.muted, fontSize: 11, marginTop: 2 }}>{agent.mobile}</div>
                  <div style={{ fontFamily: T.fontMono, fontSize: 10, color: STATUS[agent.status]?.color || T.muted, margin: "6px 0" }}>
                    ● {STATUS[agent.status]?.label || "UNKNOWN"} · {agent.speed} km/h
                  </div>
                  {agent.productName && (
                    <div style={{ fontSize: 10, color: T.muted2, fontFamily: T.fontMono, marginBottom: 6 }}>
                      Product: {agent.productName}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: T.muted2, fontFamily: T.fontMono, marginBottom: 6 }}>
                    Last reporting: {agent.timestamp || agent.lastSeen}
                  </div>
                  {agent.address && (
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, lineHeight: 1.4 }}>
                      📍 {agent.address.trim()}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn size="sm" variant="outline" onClick={() => openAlertModal(agent)}>💬 Alert</Btn>
                    <Btn size="sm" variant="ghost"   onClick={() => openHistory(agent)}>⏱ History</Btn>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Top controls */}
      <div style={styles.topBar}>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>MAP</span>
          <select
            value={mapStyle}
            onChange={e => setMapStyle(e.target.value)}
            style={styles.filterSelect}
          >
            {Object.entries(MAP_CONFIG.TILE_THEMES).map(([id, cfg]) => (
              <option key={id} value={id}>{cfg.name}</option>
            ))}
          </select>
        </div>
        <div 
          style={{ 
            ...styles.chip, 
            color: statusFilter === "online" ? T.acc3 : T.acc3, 
            borderColor: statusFilter === "online" ? "rgba(106,247,196,0.5)" : "rgba(106,247,196,0.3)",
            background: statusFilter === "online" ? "rgba(106,247,196,0.15)" : T.s1,
            cursor: "pointer"
          }}
          onClick={() => setStatusFilter(statusFilter === "online" ? "all" : "online")}
        >
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.acc3, animation: "pulse 1.5s infinite" }} />
          {statusCounts.online} ONLINE
        </div>
        <div 
          style={{ 
            ...styles.chip, 
            color: statusFilter === "idle" ? T.acc : T.muted,
            borderColor: statusFilter === "idle" ? "rgba(124,106,247,0.5)" : T.border2,
            background: statusFilter === "idle" ? "rgba(124,106,247,0.15)" : T.s1,
            cursor: "pointer"
          }}
          onClick={() => setStatusFilter(statusFilter === "idle" ? "all" : "idle")}
        >
          {statusCounts.idle} IDLE
        </div>
        <div 
          style={{ 
            ...styles.chip, 
            color: statusFilter === "moving" ? T.acc : T.acc,
            borderColor: statusFilter === "moving" ? "rgba(124,106,247,0.5)" : "rgba(124,106,247,0.4)",
            background: statusFilter === "moving" ? "rgba(124,106,247,0.15)" : T.s1,
            cursor: "pointer"
          }}
          onClick={() => setStatusFilter(statusFilter === "moving" ? "all" : "moving")}
        >
          {statusCounts.moving} MOVING
        </div>
        <div 
          style={{ 
            ...styles.chip, 
            color: statusFilter === "offline" ? T.muted2 : T.muted2,
            borderColor: statusFilter === "offline" ? "rgba(128,128,128,0.5)" : T.border2,
            background: statusFilter === "offline" ? "rgba(128,128,128,0.15)" : T.s1,
            cursor: "pointer"
          }}
          onClick={() => setStatusFilter(statusFilter === "offline" ? "all" : "offline")}
        >
          {statusCounts.offline} OFFLINE
        </div>
        {productOptions.length > 0 && (
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>PRODUCT</span>
            <select
              value={productFilter}
              onChange={e => {
                setProductFilter(e.target.value);
                setSelectedAgentIds([]); // Reset agent selection when product changes
              }}
              style={styles.filterSelect}
            >
              <option value="">All</option>
              {productOptions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
        {/* Agent Search */}
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>AGENT</span>
          <input
            type="text"
            placeholder="Search agent..."
            value={agentSearch}
            onChange={e => setAgentSearch(e.target.value)}
            style={styles.filterInput}
          />
        </div>
        {/* Agent Multi-Select */}
        {productFilteredAgents.length > 0 && (
          <div style={{ position: "relative" }} data-agent-select>
            <div style={styles.filterGroup}>
              <span style={styles.filterLabel}>SELECT</span>
              <button
                onClick={() => {
                  setAgentSelectOpen(!agentSelectOpen);
                  if (agentSelectOpen) setAgentDropdownSearch(""); // Clear search when closing
                }}
                style={{
                  ...styles.filterSelect,
                  minWidth: 150,
                  cursor: "pointer",
                  textAlign: "left",
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: agentSelectOpen ? T.s2 : "transparent"
                }}
              >
                <span style={{ fontFamily: T.fontMono, fontSize: 10 }}>
                  {selectedAgentIds.length === 0 
                    ? "Select agents..." 
                    : `${selectedAgentIds.length} selected`}
                </span>
                <span style={{ fontSize: 10, color: T.muted }}>{agentSelectOpen ? "▲" : "▼"}</span>
              </button>
              {selectedAgentIds.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAgentIds([]);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: T.muted,
                    fontSize: 14,
                    cursor: "pointer",
                    padding: "0 4px",
                    lineHeight: 1
                  }}
                  title="Clear selection"
                >
                  ×
                </button>
              )}
            </div>
            {agentSelectOpen && (
              <div style={styles.agentDropdown}>
                <div style={{ padding: 8, borderBottom: `1px solid ${T.border2}` }}>
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={agentDropdownSearch}
                    onChange={e => setAgentDropdownSearch(e.target.value)}
                    style={{
                      width: "100%",
                      background: T.s2,
                      border: `1px solid ${T.border2}`,
                      borderRadius: 6,
                      padding: "6px 8px",
                      color: T.text,
                      fontFamily: T.fontMono,
                      fontSize: 10,
                      outline: "none"
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {productFilteredAgents
                    .filter(a => {
                      if (!agentDropdownSearch.trim()) return true;
                      const search = agentDropdownSearch.toLowerCase();
                      return a.name.toLowerCase().includes(search) || 
                             a.mobile.includes(search);
                    })
                    .map(a => {
                      const isSelected = selectedAgentIds.includes(a.id);
                      return (
                        <label
                          key={a.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 12px",
                            cursor: "pointer",
                            background: isSelected ? T.s2 : "transparent",
                            borderBottom: `1px solid ${T.border2}`,
                            transition: "background 0.15s"
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = T.s2;
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAgentIds([...selectedAgentIds, a.id]);
                              } else {
                                setSelectedAgentIds(selectedAgentIds.filter(id => id !== a.id));
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: T.text, fontWeight: isSelected ? 600 : 400 }}>
                              {a.name}
                            </div>
                            <div style={{ fontSize: 9, color: T.muted, fontFamily: T.fontMono }}>
                              {a.mobile}
                            </div>
                          </div>
                          {a.productName && (
                            <div style={{ fontSize: 8, color: T.muted2, fontFamily: T.fontMono }}>
                              {a.productName}
                            </div>
                          )}
                        </label>
                      );
                    })}
                </div>
                {selectedAgentIds.length > 0 && (
                  <div style={{ padding: 8, borderTop: `1px solid ${T.border2}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 9, color: T.muted, fontFamily: T.fontMono }}>
                      {selectedAgentIds.length} selected
                    </span>
                    <button
                      onClick={() => setSelectedAgentIds([])}
                      style={{
                        background: "none",
                        border: "none",
                        color: T.acc,
                        fontSize: 9,
                        cursor: "pointer",
                        fontFamily: T.fontMono,
                        textDecoration: "underline"
                      }}
                    >
                      Clear all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>STATUS</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="idle">Idle</option>
            <option value="moving">Moving</option>
          </select>
        </div>
        {selectedAgentIds.length > 0 && (
          <div style={{ ...styles.chip, color: T.acc, borderColor: "rgba(124,106,247,0.4)", background: "rgba(124,106,247,0.1)" }}>
            ✓ {selectedAgentIds.length} SELECTED
          </div>
        )}
        {agents.filter(a => a.status === "sos").length > 0 && (
          <div style={{ ...styles.chip, color: T.sos, borderColor: "rgba(255,68,102,0.5)", background: "rgba(255,68,102,0.1)", animation: "pulse 1s infinite" }}>
            🚨 {agents.filter(a => a.status === "sos").length} SOS
          </div>
        )}
      </div>

      {/* Selected agent detail card */}
      {/* Hidden as requested */}
      {/* {selectedAgent && (
        <div className="map-detail-card" style={styles.detailCard}>
          <AgentDetailCard agent={selectedAgent} onClose={() => selectAgent(null)} openAlertModal={openAlertModal} openHistory={openHistory} />
        </div>
      )} */}

      {/* Bottom agent pills — agents with GPS */}
      {/* Hidden as requested - screen was filled with agent names */}
      {/* <div className="map-bottom-bar" style={styles.bottomBar}>
        {filteredAgents.map(a => (
          <div key={a.id}
            style={{ ...styles.agentPill, borderColor: (STATUS[a.status]?.color || T.muted) + "55", cursor: "pointer" }}
            onClick={() => selectAgent(a)}
          >
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS[a.status]?.color || T.muted, flexShrink: 0 }} />
            <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.muted }}>{a.name.split(" ")[0]}</span>
            {a.speed > 0 && <span style={{ fontFamily: T.fontMono, fontSize: 9, color: STATUS[a.status]?.color }}>{a.speed}km/h</span>}
          </div>
        ))}
      </div> */}
    </div>
  );
}

function AgentDetailCard({ agent, onClose, openAlertModal, openHistory }) {
  const s = STATUS[agent.status] || STATUS.offline;
  return (
    <div style={styles.infoCard}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: s.bg, border: `2px solid ${s.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.fontMono, fontSize: 14, fontWeight: 700, color: s.color }}>{agent.initials}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{agent.name}</div>
            <div style={{ fontSize: 11, color: T.muted }}>{agent.mobile}</div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: T.muted, fontSize: 18, cursor: "pointer" }}>×</button>
      </div>

      <Badge color={s.color} bg={s.bg} border={s.border}>● {s.label}</Badge>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, margin: "12px 0" }}>
        {[
          { label: "Speed",   val: `${agent.speed || 0} km/h` },
          { label: "Heading", val: `${agent.heading || 0}°` },
          { label: "Device",  val: agent.deviceId ? `#${agent.deviceId}` : "—" },
          { label: "City",    val: agent.city || "—" },
        ].map(m => (
          <div key={m.label} style={{ background: T.s2, borderRadius: 6, padding: "8px 10px" }}>
            <div style={{ fontSize: 9, color: T.muted, fontFamily: T.fontMono, marginBottom: 3 }}>{m.label}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: T.fontMono }}>{m.val}</div>
          </div>
        ))}
      </div>

      {agent.lat && agent.lng && (
        <div style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono, marginBottom: 6 }}>
          📍 {agent.lat?.toFixed(6)}°N, {agent.lng?.toFixed(6)}°E
        </div>
      )}

      {agent.address && (
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 12, lineHeight: 1.4 }}>
          {agent.address.trim()}
        </div>
      )}

      <div style={{ fontSize: 10, color: T.muted2, fontFamily: T.fontMono, marginBottom: 12 }}>
        Last reporting time: {agent.lastSeen}
      </div>
      <div style={{ fontSize: 10, color: T.muted2, fontFamily: T.fontMono, marginBottom: 12 }}>
        Timestamp: {agent.timestamp || agent.lastSeen}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <Btn size="sm" variant="outline" full onClick={() => openAlertModal(agent)}>💬 Send Alert</Btn>
        <Btn size="sm" variant="ghost"   full onClick={() => openHistory(agent)}>⏱ History</Btn>
      </div>
    </div>
  );
}

const styles = {
  popup:      { background: T.s1, color: T.text, fontFamily: T.fontSans, minWidth: 180, fontSize: 12 },
  topBar:     { position: "absolute", top: 14, left: 14, display: "flex", flexWrap: "wrap", gap: 8, zIndex: 1000, maxWidth: "calc(100% - 28px)" },
  chip:       { display: "flex", alignItems: "center", gap: 6, background: T.s1, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "6px 12px", fontFamily: T.fontMono, fontSize: 10, color: T.muted, cursor: "pointer", backdropFilter: "blur(10px)", letterSpacing: "0.5px", transition: "all 0.2s" },
  chipActive: { borderColor: T.acc, color: T.acc, background: "rgba(124,106,247,0.1)" },
  filterGroup:{ display: "flex", alignItems: "center", gap: 6, background: T.s1, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "6px 10px", fontFamily: T.fontMono, fontSize: 10, color: T.muted, backdropFilter: "blur(10px)" },
  filterLabel:{ fontSize: 9, color: T.muted2, letterSpacing: "1px" },
  filterSelect:{ background: "transparent", color: T.text, border: "none", outline: "none", fontFamily: T.fontMono, fontSize: 10 },
  filterInput:{ background: "transparent", color: T.text, border: "none", outline: "none", fontFamily: T.fontMono, fontSize: 10, minWidth: 120, padding: 0 },
  detailCard: { position: "absolute", top: 14, right: 14, width: 268, zIndex: 1000 },
  infoCard:   { background: "rgba(8,12,20,0.95)", border: `1px solid ${T.border2}`, borderRadius: 12, padding: 16, backdropFilter: "blur(12px)" },
  bottomBar:  { position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", zIndex: 1000, maxWidth: "70%" },
  agentPill:  { display: "flex", alignItems: "center", gap: 5, background: "rgba(8,12,20,0.9)", border: "1px solid", borderRadius: 20, padding: "5px 10px", backdropFilter: "blur(8px)", transition: "all 0.18s" },
  agentDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    minWidth: 250,
    maxWidth: 300,
    background: T.s1,
    border: `1px solid ${T.border2}`,
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    zIndex: 2000,
    backdropFilter: "blur(10px)",
    overflow: "hidden"
  },
};
